import React, { useState, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Box from '../../shared/atoms/Box';
import Text from '../../shared/atoms/Text';
import Heading from '../../shared/atoms/Heading';
import RapportDiagnosticItem, {
  mapElementValueToStatus,
} from '../../rapport/RapportDiagnosticItem';
import AngleDown16 from '../../icons/AngleDown16';
import AngleUp16 from '../../icons/AngleUp16';
import BrandIcon from './BrandIcon';
import { RJ, TITLE_FONT } from '../../rapport/rapportTokens';

const Card = styled(Box)`
  border: 1px solid ${RJ.border};
  border-radius: ${RJ.radiusLg};
  overflow: hidden;
`;

// §5.3 — AronVF sub-section title, page-scoped. Ticket #37 §2: this is a
// sub-section of "Le rapport de votre réparateur" (a 20px section heading), so
// it must read smaller. `&&` doubles specificity to override the Heading atom's
// level-driven size + its default `color: black`, locking it to 16px /#314959.
const SectionTitle = styled(Heading)`
  && {
    font-family: ${TITLE_FONT};
    font-size: 16px;
    line-height: 1.3;
    color: ${RJ.text};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  & svg {
    flex-shrink: 0;
  }
`;

const Header = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${(p) => p.theme.colors.white};
  border: none;
  cursor: pointer;
  text-align: left;
  font: inherit;

  &:hover {
    background: ${(p) => p.theme.colors.gray.lightest};
  }
`;

const Body = styled(Box)`
  padding: 12px 20px 20px 20px;
  background: ${(p) => p.theme.colors.white};
`;

// Override the global `section { margin: 5em 0; padding: 5em 0; }` rule in
// styles.less:316 (legacy CSS that applies to every <section> in the app —
// 5em = 80px each side, blowing up vertical layout). We keep the semantic
// element but neutralise the rule and drop the trailing mb on the last
// RapportDiagnosticItem so the section ends flush.
//
// DEVIS-QA-12 — clear visual break between consecutive vélos. The separator
// uses `&&& + &&&` (specificity 0,6,0) so it survives the `&&&` reset above;
// the previous `margin-top` (specificity 0,1,0) was swallowed by that reset,
// which left consecutive vélos glued together with no gap.
const VeloSection = styled(Box)`
  &&& {
    margin: 0;
    padding: 0;
  }

  &&& + &&& {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid ${(p) => p.theme.colors.gray.light};
  }

  & > [data-testid='rapport-diagnostic-item']:last-of-type {
    margin-bottom: 0;
  }
`;

// The v2-05 client payload exposes the resolved `status` on each diagnostic
// item but no `element_value` — RapportDiagnosticItem reads element_value to
// derive its badge. Bridge the two so the same atom renders both shapes.
// The API enum is UPPERCASE (OK / MONITOR / REPAIR); mapElementValueToStatus
// expects the lowercase business names (ok / to_monitor / to_repair).
const STATUS_BRIDGE = {
  OK: 'ok',
  MONITOR: 'to_monitor',
  REPAIR: 'to_repair',
};

const normalizeItem = (item) => {
  if (!item) return item;
  if (item.element_value != null && item.element_value !== '') return item;
  if (item.status) {
    const upper = String(item.status).toUpperCase();
    const mapped = STATUS_BRIDGE[upper] || String(item.status).toLowerCase();
    return { ...item, element_value: mapped };
  }
  return item;
};

// Sort order: GENERAL_NOTES always first (ticket #40 — « Note générale en
// haut du diagnostic »), then actionable items (to_repair / to_monitor), then
// OK and unknown. This is the "complete diagnostic" view — every item is
// rendered, never just summarised.
const PRIORITY = { to_repair: 0, to_monitor: 1, ok: 2 };
const sortedItems = (items) =>
  items
    .map(normalizeItem)
    .slice()
    .sort((a, b) => {
      const aIsGeneral = (a && a.element_type) === 'GENERAL_NOTES';
      const bIsGeneral = (b && b.element_type) === 'GENERAL_NOTES';
      if (aIsGeneral && !bIsGeneral) return -1;
      if (!aIsGeneral && bIsGeneral) return 1;
      const sa = mapElementValueToStatus(a.element_value);
      const sb = mapElementValueToStatus(b.element_value);
      const ra = sa in PRIORITY ? PRIORITY[sa] : 3;
      const rb = sb in PRIORITY ? PRIORITY[sb] : 3;
      return ra - rb;
    });

const isEmptyReport = (report) => {
  if (!report) return true;
  const velos = Array.isArray(report.velos) ? report.velos : [];
  if (velos.length === 0) return true;
  return velos.every(
    (v) =>
      !v ||
      !Array.isArray(v.diagnostic_items) ||
      v.diagnostic_items.length === 0,
  );
};

const totalItemCount = (report) => {
  if (!report || !Array.isArray(report.velos)) return 0;
  return report.velos.reduce(
    (acc, v) =>
      acc +
      (Array.isArray(v && v.diagnostic_items) ? v.diagnostic_items.length : 0),
    0,
  );
};

// `!= null` (not `||`) so legitimate DB ids of 0 don't cascade to the
// positional fallback and collide across velos/items.
const veloKey = (velo, idx) => {
  if (velo == null) return `velo-empty-${idx}`;
  if (velo.course_velo_id != null) return `velo-cv-${velo.course_velo_id}`;
  if (velo.velo_id != null) return `velo-v-${velo.velo_id}`;
  if (velo.id != null) return `velo-id-${velo.id}`;
  return `velo-idx-${idx}`;
};

const itemKey = (item, parentKey, idx) => {
  if (item == null) return `${parentKey}-item-empty-${idx}`;
  if (item.id != null) return `${parentKey}-item-${item.id}`;
  return `${parentKey}-item-idx-${idx}`;
};

// Per-velo section extracted so we can `useMemo(sortedItems, [items])` inside
// the iteration without violating rules-of-hooks. `memo()` bails out when the
// parent re-renders for unrelated reasons (no prop changes).
const VeloDiagnosticSection = memo(function VeloDiagnosticSection({
  velo,
  index,
  label,
}) {
  const items = Array.isArray(velo && velo.diagnostic_items)
    ? velo.diagnostic_items
    : [];
  const ordered = useMemo(() => sortedItems(items), [items]);
  if (items.length === 0) return null;

  const key = veloKey(velo, index);
  return (
    <VeloSection as="section" data-testid={`bike-diagnostic-velo-${key}`}>
      <Heading level={6} mt={0} mb={2}>
        {label}
      </Heading>
      {ordered.map((item, itemIdx) => (
        <RapportDiagnosticItem key={itemKey(item, key, itemIdx)} item={item} />
      ))}
    </VeloSection>
  );
});

VeloDiagnosticSection.propTypes = {
  velo: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
};

const BikeDiagnosticAccordion = ({ report, defaultOpen }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(!!defaultOpen);

  // totalItems shows up in the always-visible header — memoise to avoid
  // walking every velo's items on every accordion toggle.
  const totalItems = useMemo(() => totalItemCount(report), [report]);

  if (isEmptyReport(report)) return null;

  const velos = Array.isArray(report.velos) ? report.velos : [];
  const fallback = t('course.diagnostic.bike.fallback');

  return (
    <Card mb={4} data-testid="bike-diagnostic-accordion">
      <Header
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="bike-diagnostic-toggle">
        <HeaderLeft>
          <BrandIcon name="diagnostic" size={28} />
          <Box>
            <SectionTitle level={5} mt={0} mb={0}>
              {t('course.diagnostic.heading')}
            </SectionTitle>
            {totalItems > 0 && (
              <Text mb={0} mt={1} color="gray.darker" fontSize="sm">
                {t(
                  totalItems > 1
                    ? 'course.diagnostic.itemcount_other'
                    : 'course.diagnostic.itemcount_one',
                  { count: totalItems },
                )}
              </Text>
            )}
          </Box>
        </HeaderLeft>
        {open ? <AngleUp16 /> : <AngleDown16 />}
      </Header>

      {open && (
        <Body data-testid="bike-diagnostic-body">
          {velos.map((velo, idx) => (
            <VeloDiagnosticSection
              key={veloKey(velo, idx)}
              velo={velo}
              index={idx}
              label={(velo && velo.velo_label) || `${fallback} n°${idx + 1}`}
            />
          ))}
        </Body>
      )}
    </Card>
  );
};

BikeDiagnosticAccordion.propTypes = {
  report: PropTypes.shape({
    velos: PropTypes.arrayOf(
      PropTypes.shape({
        course_velo_id: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        velo_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        velo_label: PropTypes.string,
        diagnostic_items: PropTypes.array,
        prestations: PropTypes.array,
        pieces: PropTypes.array,
      }),
    ),
  }),
  defaultOpen: PropTypes.bool,
};

BikeDiagnosticAccordion.defaultProps = {
  report: null,
  defaultOpen: false,
};

export default BikeDiagnosticAccordion;
